/**
 * ClientDetailsConfirm renderer · iter303ai-fix9
 *
 * Slim inline confirm-card for Appraisal Booked · Schedule Appraisal · Action 7.
 * Loads the client by captured_data.client_id and shows an editable-in-place
 * card with just the contact + address fields — no appraisals / notes /
 * activities noise from the full ClientDetailPage.
 *
 * The card supports Save (PATCH /clients/{id}) and Confirm & Continue (advance).
 * Operators can review-only (just click Continue) or edit + Save + Continue.
 */
import { requestDriveRenameCheck } from '@/components/drive/DriveRenamePrompt';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { User, Save, CheckCircle2, Loader2 } from 'lucide-react';
import { advanceOnEnter } from '../intake/scriptEditControls';
import NameTypeahead from '../intake/NameTypeahead';

const api = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`,
});
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const FIELDS = [
  ['name_first',       'First name',    'typeahead', 'first_name'],
  ['name_last',        'Last name',     'typeahead', 'last_name'],
  ['phone_mobile',     'Mobile phone'],
  ['phone_home',       'Home phone'],
  ['email',            'Email'],
  ['address_num',      'Street number'],
  ['address_street',   'Street name'],
  ['address_suburb',   'Suburb'],
  ['address_city',     'City'],
  ['address_postcode', 'Postcode'],
];

export default function ClientDetailsConfirm({ action, capturedData, onNext, onBack, onCancel, canBack, continueLabel = 'Confirm & Continue →' }) {
  const clientId = capturedData?.client_id;
  const [form, setForm] = useState({});
  const [original, setOriginal] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    if (!clientId) { setLoading(false); return; }
    setLoading(true);
    try {
      const { data } = await api.get(`/clients/${clientId}`);
      const seed = {};
      for (const [k] of FIELDS) seed[k] = data[k] || '';
      setForm(seed); setOriginal(seed);
    } catch (e) { setErr(e?.response?.data?.detail || e.message); }
    finally { setLoading(false); }
  }, [clientId]);

  useEffect(() => { load(); }, [load]);

  // iter303aj-fix5 — Postcode auto-fill (silent) when suburb + city known
  // and postcode is empty. Operator can Tab to confirm or type over.
  const postcodeRef = useRef({ suburb: '', city: '' });
  useEffect(() => {
    const suburb = (form.address_suburb || '').trim();
    const city   = (form.address_city   || '').trim();
    if (!suburb || !city) return;
    if ((form.address_postcode || '').trim()) return;
    if (postcodeRef.current.suburb === suburb.toLowerCase()
     && postcodeRef.current.city   === city.toLowerCase()) return;
    postcodeRef.current = { suburb: suburb.toLowerCase(), city: city.toLowerCase() };
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/postcodes/lookup', { params: { suburb, city } });
        if (cancelled) return;
        if (data?.postcode) {
          setForm(f => f.address_postcode ? f : { ...f, address_postcode: data.postcode });
        }
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [form.address_suburb, form.address_city, form.address_postcode]);

  const isDirty = Object.keys(form).some(k => (form[k] || '') !== (original[k] || ''));

  const save = async () => {
    if (!isDirty) return;
    setSaving(true); setErr('');
    try {
      await api.put(`/clients/${clientId}`, form);
      requestDriveRenameCheck({ client_id: clientId });
      setOriginal({ ...form });
      setSaved(true);
      setTimeout(() => setSaved(false), 1800);
    } catch (e) { setErr(e?.response?.data?.detail || e.message); }
    finally { setSaving(false); }
  };

  if (!clientId) {
    return (
      <div className="p-6 bg-rose-50 border border-rose-200 rounded text-sm text-rose-700">
        No client linked to this run — cannot confirm client details.
        {canBack && <button onClick={onBack} className="ml-3 underline">← Back</button>}
      </div>
    );
  }

  return (
    <div data-testid="wf-client-details-confirm" onKeyDown={advanceOnEnter}>
      <div className="bg-gradient-to-br from-sky-50 to-white border-l-4 border-sky-400 rounded-r-lg p-5 mb-5">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-sky-600 font-semibold mb-1">
          <User size={12} /> Confirm Client Details
        </div>
        <div className="text-slate-700 text-sm">
          Verify the client&apos;s contact and address details are correct. Edit any field, then Save and Continue.
        </div>
      </div>

      {loading ? (
        <div className="p-4 text-sm text-slate-400 italic">Loading client…</div>
      ) : (
        <div className="mb-5 border border-slate-200 rounded-lg bg-white p-4 grid grid-cols-2 gap-3">
          {FIELDS.map(([key, label, kind, source]) => (
            <label key={key} className="flex flex-col gap-1">
              <span className="text-[11px] uppercase tracking-widest text-slate-400 font-semibold">{label}</span>
              {kind === 'typeahead' ? (
                <NameTypeahead
                  value={form[key] || ''}
                  onChange={v => setForm(f => ({ ...f, [key]: v }))}
                  suggestionsSource={source}
                  placeholder="Type to search or add new…"
                  testId={`wf-client-field-${key}`}
                />
              ) : (
                <input
                  type="text"
                  value={form[key] || ''}
                  onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                  data-testid={`wf-client-field-${key}`}
                  className="border border-slate-200 rounded px-2 py-1.5 text-sm focus:outline-none focus:border-sky-400 focus:ring-1 focus:ring-sky-100"
                />
              )}
            </label>
          ))}
        </div>
      )}

      {err && (
        <div className="mb-3 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded p-3">{err}</div>
      )}

      <div className="flex items-center gap-3">
        {canBack && (
          <button onClick={onBack} data-testid="wf-back-btn"
            className="px-4 py-2 text-slate-500 hover:text-slate-700">← Back</button>
        )}
        {onCancel && (
          <button onClick={onCancel} data-testid="wf-cancel-btn"
            className="px-4 py-2 text-rose-500 hover:text-rose-700 text-sm">Cancel</button>
        )}
        <span className="flex-1" />
        <button
          onClick={save}
          disabled={!isDirty || saving || loading}
          data-testid="wf-client-save-btn"
          className="inline-flex items-center gap-1.5 px-4 py-2 bg-white border-2 border-sky-500 text-sky-700 font-semibold rounded hover:bg-sky-50 disabled:opacity-40 disabled:cursor-not-allowed">
          {saving ? <Loader2 size={14} className="animate-spin" /> : saved ? <CheckCircle2 size={14} className="text-emerald-500" /> : <Save size={14} />}
          {saving ? 'Saving…' : saved ? 'Saved' : 'Save changes'}
        </button>
        <button
          onClick={async () => {
            // Pre-UAT v10.11 fix (Feb 2026 · UAT audit Pass 2 F-P2-P1-001)
            // — sibling confirm cards (PropertyDetailsConfirm,
            // TenantAgentConfirm) auto-save on Continue. This one didn't,
            // so typed-but-unsaved client edits were silently discarded.
            // Client PUT endpoint is merge-safe (v10.9 `exclude_unset`),
            // so no clobber risk here.
            if (isDirty && !saving) { try { await save(); } catch (_e) { /* stay on card if save failed */ return; } }
            onNext();
          }}
          disabled={saving}
          data-testid="wf-client-continue-btn"
          className="px-6 py-2 bg-sky-600 text-white font-semibold rounded hover:bg-sky-700 disabled:opacity-40">
          {continueLabel}
        </button>
      </div>
    </div>
  );
}
